<!-- Group — one group's pages from everyone, oldest at the top, with the
     membership changes ("JESS JOINED") between them. Each incoming page says
     who sent it, above the bubble. Screens role. -->
<template>
  <IonPage>
    <AppHeader :title="title" back="/tabs/pager">
      <IonButtons slot="end">
        <IonButton :disabled="!group" @click="membersOpen = true">Members</IonButton>
      </IonButtons>
    </AppHeader>

    <IonContent ref="content" :fullscreen="true" class="pm-scr-content">
      <div class="pm-scr-pad">
        <button
          v-if="group"
          type="button"
          class="pm-scr-namebar"
          :aria-label="`${group.name}, ${countLine}. Show members`"
          @click="membersOpen = true"
        >
          <span class="pm-scr-namebar-main">{{ countLine }}</span>
          <span class="pm-scr-muted pm-scr-small">{{ memberLine }}</span>
        </button>

        <section v-else class="pm-scr-notice" aria-label="Not in this group">
          <p>You're not in {{ title }} any more, so you won't get its pages. What it sent before stays here.</p>
        </section>

        <ol v-if="thread.length" class="pm-scr-thread" aria-label="Pages">
          <template v-for="p in thread" :key="p.id">
            <li v-if="p.system" class="pm-scr-sysline">
              <span>{{ p.text }}</span>
              <time class="sr-only" :datetime="iso(p.sentAt)">{{ when(p.sentAt) }}</time>
            </li>
            <li
              v-else
              class="pm-scr-msg"
              :class="p.dir === 'out' ? 'pm-scr-msg--out' : 'pm-scr-msg--in'"
            >
              <button
                v-if="p.dir === 'in'"
                type="button"
                class="pm-scr-sender"
                :aria-label="`${senderName(p)}. Save as`"
                @click="promptName(p.peer)"
              >{{ senderName(p) }}</button>
              <div
                class="pm-scr-bubble"
                :class="p.dir === 'out' ? 'pm-scr-bubble--out' : ['pm-scr-bubble--in', glassClass]"
              >
                <span class="sr-only">{{ p.dir === 'out' ? 'You' : senderName(p) }}:</span>
                <PageText :body="p.body" />
                <p v-if="hint(p.body)" class="pm-scr-hint">{{ hint(p.body) }}</p>
              </div>
              <div class="pm-scr-msg-meta">
                <time :datetime="iso(p.sentAt)">{{ when(p.sentAt) }}</time>
                <StatusMark v-if="p.dir === 'out'" :page="p" />
                <span v-if="p.locked" class="pm-scr-tag">LOCKED</span>
                <button
                  type="button"
                  class="pm-scr-more"
                  :aria-label="`More for the page ${p.dir === 'out' ? 'you sent' : 'from ' + senderName(p)} at ${when(p.sentAt)}`"
                  @click="openMenu(p, $event)"
                >
                  <IonIcon :icon="ellipsisHorizontal" aria-hidden="true" />
                </button>
              </div>
            </li>
          </template>
        </ol>

        <div v-if="group && !hasPages" class="pm-scr-empty">
          <p>No pages in {{ title }} yet. One page here reaches all {{ group.members.length - 1 }} of them.</p>
        </div>
      </div>

      <ActionRail v-if="group">
        <form v-if="twoWay" class="pm-scr-reply" @submit.prevent="reply.run()">
          <label class="sr-only" :for="`${uid}-group-reply`">Page {{ title }}</label>
          <input
            :id="`${uid}-group-reply`"
            :value="replyText"
            class="pm-scr-input pm-scr-input--lcd"
            type="text"
            placeholder="PAGE THE GROUP"
            autocomplete="off"
            enterkeyhint="send"
            :aria-describedby="`${uid}-group-count`"
            @input="onReplyInput"
          >
          <span :id="`${uid}-group-count`" class="pm-scr-counter pm-scr-counter--inline" :class="{ 'is-warn': replyCount >= 150 }">
            {{ replyCount }}/{{ MAX_GRAPHEMES }}
          </span>
          <button type="submit" class="sr-only" tabindex="-1" aria-hidden="true" />
          <IonButton type="button" :disabled="!replyText.trim() || reply.busy.value" @click="reply.run()">SEND</IonButton>
        </form>
        <IonButton v-else expand="block" @click="compose()">Page the group</IonButton>
        <p v-if="reply.error.value" class="field-error" role="alert">{{ reply.error.value }}</p>
      </ActionRail>
    </IonContent>

    <GroupMembersSheet :is-open="membersOpen" :group-id="id" @close="membersOpen = false" @left="afterLeave" />
  </IonPage>
</template>

<script setup>
import { userMessage } from '../components/user-message.js';
import { ref, computed, watch, nextTick, onMounted, useId } from 'vue';
import { useRouter } from 'vue-router';
import {
  IonPage, IonContent, IonButtons, IonButton, IonIcon,
  actionSheetController, alertController, onIonViewDidEnter,
} from '@ionic/vue';
import { ellipsisHorizontal } from 'ionicons/icons';
import { AppHeader, ActionRail, useAction, toast, announce } from '../core/ui/index.js';
import {
  me, settings, currentDevice, drafts,
  groupById, groupLabel, groupThread, groupDraftKey, friendName, senderName, markRead, toggleLock, deletePage, reportPage,
  sendGroupPage,
} from '../store.js';
import { whenLong } from '../components/when.js';
import { countGraphemes, MAX_GRAPHEMES } from '../pm/text.js';
import { hintFor } from '../pm/codebook.js';
import { sharePageImage } from '../pm/share-image.js';
import { promptName } from '../components/name-prompt.js';
import PageText from '../components/PageText.vue';
import StatusMark from '../components/StatusMark.vue';
import GroupMembersSheet from '../components/GroupMembersSheet.vue';

const uid = `pm${useId()}`;

const props = defineProps({ id: { type: String, required: true } });
const router = useRouter();
const content = ref(null);
const membersOpen = ref(false);

const group = computed(() => groupById(props.id));
const thread = computed(() => groupThread(props.id) ?? []);
const hasPages = computed(() => thread.value.some((p) => !p.system));
const title = computed(() => groupLabel(props.id, thread.value.find((p) => p.groupName)?.groupName));
const twoWay = computed(() => currentDevice.value?.family === 'twoway');
const countLine = computed(() => (group.value ? `${group.value.members.length} pagers` : ''));
// "You, Sam, Jess" — names, never numbers, once there are names.
const memberLine = computed(() => (group.value?.members ?? [])
  .map((m) => (m.number === me.value.number ? 'You' : friendName(m.number)))
  .join(', '));

const glassClass = computed(() => {
  if (settings.highContrast) return 'pm-scr-glass--hc';
  return `pm-scr-glass--${currentDevice.value?.lcd || 'classic'}`;
});

function compose() { router.push(`/tabs/compose/group-${props.id}`); }
function afterLeave() {
  membersOpen.value = false;
  router.replace('/tabs/pager');
}

const iso = (ts) => (ts ? new Date(ts).toISOString() : '');
const when = (ts) => (ts ? whenLong(ts) : '');
function hint(bodyText) {
  const meaning = hintFor(bodyText || '');
  if (!meaning) return '';
  const code = String(bodyText).trim().split(/\s+/)[0];
  return `${code} = ${meaning.toUpperCase()}`;
}

// ── read + scroll ───────────────────────────────────────────────────────
function markShownRead() {
  for (const p of thread.value) if (!p.system && p.dir === 'in' && !p.read) markRead(p.id);
}
async function toBottom(duration = 0) {
  await nextTick();
  content.value?.$el?.scrollToBottom?.(duration);
}
onMounted(() => { markShownRead(); toBottom(); });
onIonViewDidEnter(() => { markShownRead(); toBottom(); });
watch(() => thread.value.length, (n, old) => {
  markShownRead();
  toBottom(old ? 200 : 0);
});

// ── inline send (two-way devices) ──────────────────────────────────────
const draftKey = computed(() => groupDraftKey(props.id));
const replyText = ref(drafts[draftKey.value] || '');
const replyCount = computed(() => countGraphemes(replyText.value));
let draftTimer = 0;
function onReplyInput(e) {
  const next = e.target.value;
  if (countGraphemes(next) > MAX_GRAPHEMES) {
    e.target.value = replyText.value;
    announce(`Pages stop at ${MAX_GRAPHEMES} characters.`);
    return;
  }
  replyText.value = next;
  clearTimeout(draftTimer);
  draftTimer = setTimeout(() => { drafts[draftKey.value] = replyText.value; }, 300);
}
const reply = useAction(async () => {
  const text = replyText.value.trim();
  if (!text) return;
  const r = await sendGroupPage(props.id, text);
  if (!r?.ok) throw new Error(userMessage(r, "Couldn't send that page. Try again."));
  clearTimeout(draftTimer);
  replyText.value = '';
  drafts[draftKey.value] = '';
  toast(r.queued ? 'QUEUED — WILL SEND WHEN ONLINE' : `SENT TO ${title.value} ✓`);
}, { weight: 'pop' });

// ── the ⋯ menu ─────────────────────────────────────────────────────────
async function openMenu(page, event) {
  const trigger = event?.currentTarget;
  const buttons = [
    { text: 'Copy', data: 'copy' },
    { text: page.locked ? 'Unlock' : 'Lock', data: 'lock' },
    { text: 'Share as image', data: 'share' },
  ];
  if (page.dir === 'in') buttons.push({ text: `Save ${senderName(page)} as…`, data: 'name' });
  buttons.push({ text: 'Delete', role: 'destructive', data: 'delete' });
  if (page.dir === 'in') buttons.push({ text: 'Report', role: 'destructive', data: 'report' });
  buttons.push({ text: 'Cancel', role: 'cancel' });

  const sheet = await actionSheetController.create({ header: 'Page', buttons });
  await sheet.present();
  const { data } = await sheet.onDidDismiss();
  const choice = data?.action ?? data;
  try {
    if (choice === 'copy') {
      await navigator.clipboard.writeText(page.body);
      toast('Copied');
    } else if (choice === 'lock') {
      await toggleLock(page.id);
    } else if (choice === 'share') {
      await sharePageImage(page, currentDevice.value);
    } else if (choice === 'name') {
      await promptName(page.peer);
    } else if (choice === 'delete') {
      await deletePage(page.id);
    } else if (choice === 'report') {
      await report(page);
    }
  } catch (e) {
    toast(e?.message || 'That did not work. Try again.');
  }
  try { trigger?.focus?.(); } catch { /* gone */ }
}

const REASONS = ['Spam', 'Harassment or bullying', 'Threats or violence', 'Something else'];
async function report(page) {
  const who = senderName(page);
  const sheet = await actionSheetController.create({
    header: 'Why are you reporting this page?',
    buttons: [...REASONS.map((r) => ({ text: r, data: r })), { text: 'Cancel', role: 'cancel' }],
  });
  await sheet.present();
  const { data: reason } = await sheet.onDidDismiss();
  if (!reason || typeof reason !== 'string') return;
  const confirm = await alertController.create({
    header: `Report and block ${who}?`,
    message: "We'll get a copy of this page. Reporting also blocks them — their pages, in this group too, stop reaching you. They aren't told.",
    buttons: [{ text: 'Cancel', role: 'cancel' }, { text: 'Report and block', role: 'destructive' }],
  });
  await confirm.present();
  const { role } = await confirm.onDidDismiss();
  if (role !== 'destructive') return;
  const r = await reportPage(page, reason);
  if (r && r.ok === false) throw new Error(userMessage(r, "Couldn't send the report. Try again."));
  toast(`Reported. ${who} is blocked.`);
}
</script>
